import { useState } from 'react'
import { createPortal } from 'react-dom'
import { ToolTip } from '../../../../components/toolTip/ToolTip'
import { translations } from '../../../../configs/translations'
import { Language } from '../../../../context/LanguageContext'
import { VehicleSpecData } from '../VehicleSpec.types'
import './FullVehicleSpec.css'
import { FullVehicleSpecItem, FullVehicleSpecItemField } from './item/FullVehicleSpecItem'
import { ToolTipFullVehicleSpec } from './tooltip/ToolTipFullVehicleSpec'

export interface FullVehicleSpecProps {
	vehicleSpec: VehicleSpecData
}

export const FullVehicleSpec = ({
	vehicleSpec
}: FullVehicleSpecProps) => {

	const [language, setLanguage] = useState<Language>('ru')
	const t = translations[language]

	const [mouseX, setMouseX] = useState(0)
	const [mouseY, setMouseY] = useState(0)
	const [hoveredField, setHoveredField] = useState<FullVehicleSpecItemField | null>(null)

	const handleHover = (event: React.MouseEvent, field: FullVehicleSpecItemField) => {
		setMouseX(event.clientX)
		setMouseY(event.clientY)
		setHoveredField(field)
	}

	const handleLeave = () => setHoveredField(null)

	return (<>
		<div className='full-vehicle-spec'>

			<div className='full-vehicle-spec-content'>

				<FullVehicleSpecItem
					icon='power'
					title={t.weight_and_aerodynamics}
					fields={[
						{
							name: t.weight,
							value_sign: t.kg,
							value: vehicleSpec.fullInfo.weight,
							baseValue: vehicleSpec.baseInfo.weight,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'MASS'),
							description: t.spec_info_weight
						},
						{
							name: t.aerodynamics,
							value_sign: t.aerodynamics_value,
							value: vehicleSpec.fullInfo.aerodynamics,
							baseValue: vehicleSpec.baseInfo.aerodynamics,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'DRAG'),
							description: t.spec_info_aerodynamics
						},
					]}
					onHover={handleHover}
					onLeave={handleLeave}
				/>

				<FullVehicleSpecItem
					icon='speedometer'
					title={t.dynamics}
					fields={[
						{
							name: t.power,
							value: vehicleSpec.fullInfo.power,
							value_sign: t.horse_powers_shortened,
							baseValue: vehicleSpec.baseInfo.power,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'POWER'),
							description: t.spec_info_power
						},
						{
							name: t.torque,
							value: vehicleSpec.fullInfo.torque,
							value_sign: t.newton_meters_shortened,
							baseValue: vehicleSpec.baseInfo.torque,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'TORQUE'),
							description: t.spec_info_torque
						},
						{
							name: t.top_speed_shortened,
							value: vehicleSpec.fullInfo.topSpeed,
							value_sign: t.kmh,
							baseValue: vehicleSpec.baseInfo.topSpeed,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'TOP_SPEED'),
							description: t.spec_info_speedometer
						},
						{
							name: t.redline_speed,
							value: vehicleSpec.fullInfo.redlineSpeed,
							value_sign: t.milliseconds,
							baseValue: vehicleSpec.baseInfo.redlineSpeed,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'REDLINE'),
							description: t.spec_info_redline_speed
						},
						{
							name: t.gears_amount,
							value: vehicleSpec.fullInfo.gears,
							value_sign: "",
							baseValue: vehicleSpec.baseInfo.gears,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'GEARS'),
							description: t.spec_info_gears_amount
						},
						{
							name: t.clutch_speed,
							value: vehicleSpec.fullInfo.clutchSpeed,
							value_sign: t.milliseconds,
							baseValue: vehicleSpec.baseInfo.clutchSpeed,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'SHIFT_TIME'),
							description: t.spec_info_clutch_speed
						},
					]}
					onHover={handleHover}
					onLeave={handleLeave}
				/>

				<FullVehicleSpecItem
					icon='brake'
					title={t.brakes}
					fields={[
						{
							name: t.peak_brake,
							value: vehicleSpec.fullInfo.peakBrakes,
							value_sign: "g",
							baseValue: vehicleSpec.baseInfo.peakBrakes,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'BRAKE_100'),
							description: t.spec_info_peak_brake
						},
						{
							name: t.brake_balance,
							value: vehicleSpec.fullInfo.brakesBalance,
							value_sign: "%",
							baseValue: vehicleSpec.baseInfo.brakesBalance,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'BRAKE_BALANCE'),
							description: t.spec_info_brake_balance
						},
						{
							name: t.handbrake,
							value: vehicleSpec.fullInfo.handbrake,
							value_sign: t.acceleration_value,
							baseValue: vehicleSpec.baseInfo.handbrake,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'HANDBRAKE'),
							description: t.spec_info_handbrake
						},
					]}
					onHover={handleHover}
					onLeave={handleLeave}
				/>

				<FullVehicleSpecItem
					icon='tyres'
					title={t.tyre}
					fields={[
						{
							name: t.grip_peak,
							value: vehicleSpec.fullInfo.gripPeak,
							value_sign: "g",
							baseValue: vehicleSpec.baseInfo.gripPeak,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'GRIP_MAX'),
							description: t.spec_info_grip_peak
						},
						{
							name: t.traction_grip,
							value: vehicleSpec.fullInfo.tractionGrip,
							value_sign: "g",
							baseValue: vehicleSpec.baseInfo.tractionGrip,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'GRIP_SLIDE'),
							description: t.spec_info_traction_grip
						},
						{
							name: t.traction_bias_front,
							value: vehicleSpec.fullInfo.tractionBiasFront,
							baseValue: vehicleSpec.baseInfo.tractionBiasFront,
							value_sign: "%",
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'GRIP_BALANCE'),
							description: t.spec_info_traction_bias_front
						},
					]}
					onHover={handleHover}
					onLeave={handleLeave}
				/>


				<FullVehicleSpecItem
					icon='steering_wheel'
					title={t.steering_wheel}
					fields={[
						{
							name: t.rotation_radius,
							value: vehicleSpec.fullInfo.rotationRadius,
							baseValue: vehicleSpec.baseInfo.rotationRadius,
							value_sign: t.meters_shortened,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'TURN_RADIUS'),
							description: t.spec_info_radius
						},
						{
							name: t.steering_wheel_reaction,
							value: vehicleSpec.fullInfo.steeringWheelReaction,
							baseValue: vehicleSpec.baseInfo.steeringWheelReaction,
							value_sign: t.milliseconds,
							description: t.spec_info_steering_wheel_reaction,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'YAW_RESPONSE')
						},
					]}
					onHover={handleHover}
					onLeave={handleLeave}
				/>

				<FullVehicleSpecItem
					icon='suspension'
					title={t.suspension}
					fields={[
						{
							name: t.suspension_force,
							value: vehicleSpec.fullInfo.suspensionForce,
							baseValue: vehicleSpec.baseInfo.suspensionForce,
							value_sign: t.hertz,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'SPRING_RATE'),
							description: t.spec_info_suspension_force
						},
						{
							name: t.suspension_raise,
							value: vehicleSpec.fullInfo.suspensionRaise,
							baseValue: vehicleSpec.baseInfo.suspensionRaise,
							value_sign: t.millimeters_shortened,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'RIDE_HEIGHT'),
							description: t.spec_info_suspension_raise
						},
						{
							name: t.suspension_upper_limit,
							value: vehicleSpec.fullInfo.suspensionUpperLimit,
							baseValue: vehicleSpec.baseInfo.suspensionUpperLimit,
							value_sign: t.millimeters_shortened,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'TRAVEL'),
							description: t.spec_info_suspension_upper_limit
						},
					]}
					onHover={handleHover}
					onLeave={handleLeave}
				/>

				<FullVehicleSpecItem
					icon='car_body'
					title={t.car_body}
					fields={[
						{
							name: t.centre_of_mass_offset_z,
							value: vehicleSpec.fullInfo.centreOfMassOffsetZ,
							baseValue: vehicleSpec.baseInfo.centreOfMassOffsetZ,
							value_sign: t.millimeters_shortened,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'COG_HEIGHT'),
							description: t.spec_info_centre_of_mass_offset_z
						},
						{
							name: t.centre_of_mass_offset_y,
							value: vehicleSpec.fullInfo.centreOfMassOffsetY,
							baseValue: vehicleSpec.baseInfo.centreOfMassOffsetY,
							value_sign: '%',
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'WEIGHT_DIST'),
							description: t.spec_info_centre_of_mass_offset_y
						},
						{
							name: t.body_damage,
							value: vehicleSpec.fullInfo.bodyDamage,
							baseValue: vehicleSpec.baseInfo.bodyDamage,
							value_sign: "HP",
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'BODY_STRENGTH'),
							description: t.spec_info_body_damage
						},
						{
							name: t.tank_volume,
							value: vehicleSpec.fullInfo.tankVolume,
							baseValue: vehicleSpec.baseInfo.tankVolume,
							value_sign: t.liters_short,
							effects: vehicleSpec.effects.filter((effect) => effect.param === 'TANK'),
							description: t.spec_info_tank_volume
						},
					]}
					onHover={handleHover}
					onLeave={handleLeave}
				/>


			</div>

		</div>

		{createPortal(
			<ToolTip
				style='top'
				position={{ x: mouseX, y: mouseY }}
				content={hoveredField ?
					<ToolTipFullVehicleSpec
						name={hoveredField.name}
						baseValue={hoveredField.baseValue}
						effects={hoveredField.effects}
						value_sign={hoveredField.value_sign}
						description={hoveredField.description}
					/> : ''
				}
				isShown={hoveredField !== null}
			/>,
			document.body
		)}
	</>)

}